/**
 * Staff "Generate invoice" (#36678) is asynchronous: the request queues the invoice PDF, which the docs
 * engine saves to the policy's documents a little later. These helpers find that new PDF so the
 * modal can offer it for viewing and printing as soon as it lands.
 */

// Invoice PDFs are saved as docs/{accountId}/customer/{policyId}/{timestamp}-{F|X|Y|A|...}_INVC.pdf.
const INVOICE_FILE = /_INVC\.pdf$/;

const POLL_INTERVAL_MS = 5000;
// Rendering through Zywave and converting to PDF usually takes well under a minute.
const POLL_ATTEMPTS = 24;

const defaultSleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/** The paths of a policy's invoice PDFs among the account's regular documents. */
export function invoicePathsForPolicy(regularDocuments, policyId) {
  return (regularDocuments || [])
    .map((document) => document.path)
    .filter((path) => path?.includes(`/customer/${policyId}/`) && INVOICE_FILE.test(path));
}

/**
 * Re-reads the account's documents until an invoice PDF for the policy appears that was not in
 * `knownPaths` (the invoices listed before the request). Returns its path, or null if none appears
 * within the allowed attempts or the caller cancels.
 */
export async function waitForNewInvoice({
  refresh,
  readDocuments,
  policyId,
  knownPaths,
  attempts = POLL_ATTEMPTS,
  intervalMs = POLL_INTERVAL_MS,
  sleep = defaultSleep,
  isCancelled = () => false
}) {
  const known = new Set(knownPaths);
  for (let attempt = 0; attempt < attempts; attempt += 1) {
    await sleep(intervalMs);
    if (isCancelled()) {
      return null;
    }
    await refresh();
    if (isCancelled()) {
      return null;
    }
    const newPath = invoicePathsForPolicy(readDocuments(), policyId).find((path) => !known.has(path));
    if (newPath) {
      return newPath;
    }
  }
  return null;
}
